import { useEffect, type RefObject } from 'react';

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// The Home design's two page-level behaviours, scoped to the home root:
//  - scroll reveal: an element marked `.rv` gains `.seen` (staggered 60 ms by position)
//    when 12% of it is on screen. Reduced motion, a browser without IntersectionObserver
//    and a 3 s safety net all reveal everything at once, so nothing can stay hidden;
//  - rail drift: a `[data-auto]` rail steps 280 px every 3.2 s, bouncing at its ends, and
//    stands still under the pointer, with focus inside, off-screen, in a hidden tab and
//    under reduced motion.
// Re-run `deps` change (data arrived, so new `.rv` nodes exist).
export function useHxMotion(root: RefObject<HTMLElement | null>, deps: readonly unknown[]): void {
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const pending = () => Array.from(el.querySelectorAll<HTMLElement>('.rv:not(.seen)'));
    const revealAll = () => pending().forEach((n) => n.classList.add('seen'));
    if (reducedMotion() || typeof IntersectionObserver === 'undefined') {
      revealAll();
    }
    const io =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(
            (entries) =>
              entries.forEach((e) => {
                if (e.isIntersecting) {
                  e.target.classList.add('seen');
                  io?.unobserve(e.target);
                }
              }),
            { threshold: 0.12 },
          );
    pending().forEach((n, i) => {
      n.style.transitionDelay = `${(i % 6) * 60}ms`;
      io?.observe(n);
    });
    const safety = setTimeout(revealAll, 3000);

    const timers: number[] = [];
    if (!reducedMotion()) {
      el.querySelectorAll<HTMLElement>('.hs[data-auto]').forEach((rail) => {
        let dir = 1;
        let visible = true;
        const vis = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((es) => es.forEach((e) => (visible = e.isIntersecting)));
        vis?.observe(rail);
        const id = window.setInterval(() => {
          if (!visible || document.hidden || rail.matches(':hover') || rail.contains(document.activeElement)) return;
          if (typeof rail.scrollBy !== 'function') return;
          rail.scrollBy({ left: 280 * dir, behavior: 'smooth' });
          if (rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 10) dir = -1;
          if (rail.scrollLeft <= 0) dir = 1;
        }, 3200);
        timers.push(id);
      });
    }
    return () => {
      clearTimeout(safety);
      io?.disconnect();
      timers.forEach((id) => clearInterval(id));
    };
  }, deps);
}
